import { Box, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { BEARISH_BUCKETS, BULLISH_BUCKETS, bucketTitle, fmtPct, signed, statsOf } from '../logic';
import { GrowthPill } from './BucketCard';

const ORDER = [...BEARISH_BUCKETS, 0, ...[...BULLISH_BUCKETS].reverse()]; // -4 ... 0 ... +4

export default function SummaryHeader({ groups, total }) {
  const theme = useTheme();
  const bull = statsOf(BULLISH_BUCKETS.flatMap((b) => groups.get(b) ?? []));
  const bear = statsOf(BEARISH_BUCKETS.flatMap((b) => groups.get(b) ?? []));
  const neutral = groups.get(0)?.length ?? 0;
  const pct = (n) => (total.count ? `${Math.round((n / total.count) * 100)}% of universe` : '');

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'grid', gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        }}
      >
        <Kpi
          icon={<TrendingUpIcon />} color={theme.palette.success.main} label="Bullish"
          value={bull.count} sub={pct(bull.count)}
          foot={<>avg win {bull.avgWinRate == null ? '—' : `${bull.avgWinRate.toFixed(1)}%`}</>}
        />
        <Kpi
          icon={<TrendingDownIcon />} color={theme.palette.error.main} label="Bearish"
          value={bear.count} sub={pct(bear.count)}
          foot={<>avg win {bear.avgWinRate == null ? '—' : `${bear.avgWinRate.toFixed(1)}%`}</>}
        />
        <Kpi
          icon={<RemoveIcon />} color={theme.palette.text.secondary} label="Neutral"
          value={neutral} sub={pct(neutral)} foot="trend score 0"
        />
        <Kpi
          icon={<ShowChartIcon />} color={theme.palette.primary.main} label="Avg growth · filtered"
          value={<GrowthPill value={total.avgGrowth} large />}
          sub={`${total.count} symbols · equal-weight`}
          foot={<><Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>▲ {total.advancers}</Box>{'  '}<Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>▼ {total.decliners}</Box></>}
        />
      </Box>

      {/* Distribution -4 ... +4 - click a segment to jump to its bucket */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Trend distribution</Typography>
          <Typography variant="caption" color="text.secondary">bearish ← → bullish · click to jump</Typography>
        </Stack>
        <Stack direction="row" sx={{ height: 34, borderRadius: 2, overflow: 'hidden', gap: '2px' }}>
          {ORDER.map((b) => {
            const n = groups.get(b)?.length ?? 0;
            if (!n) return null;
            const base = b === 0 ? theme.palette.text.secondary : b > 0 ? theme.palette.success.main : theme.palette.error.main;
            const strength = Math.abs(b);
            const bg = b === 0 ? alpha(base, 0.25) : alpha(base, 0.32 + strength * 0.16);
            return (
              <Tooltip key={b} title={`${signed(b)} · ${bucketTitle(b)} · ${n} symbols · ${fmtPct(statsOf(groups.get(b) ?? []).avgGrowth)}`}>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => document.getElementById(b === 0 ? 'bucket-neutral' : `bucket-${b}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.click(); }}
                  sx={{
                    flexGrow: n, flexBasis: 0, minWidth: 30, display: 'grid', placeItems: 'center', cursor: 'pointer',
                    bgcolor: bg, color: strength >= 3 ? theme.palette.getContrastText(base) : theme.palette.text.primary,
                    fontSize: 12, fontWeight: 800, transition: 'filter .15s, transform .15s',
                    '&:hover, &:focus-visible': { filter: 'brightness(1.08)', outline: 'none' },
                  }}
                >
                  {n}
                </Box>
              </Tooltip>
            );
          })}
        </Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
          {['-4', '-3', '-2', '-1', '0', '+1', '+2', '+3', '+4'].map((l) => (
            <Typography key={l} variant="caption" color="text.disabled" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 10 }}>{l}</Typography>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
}

function Kpi({ icon, color, label, value, sub, foot }) {
  return (
    <Paper
      sx={{
        p: 2, position: 'relative', overflow: 'hidden',
        background: (t) => `linear-gradient(135deg, ${alpha(color, t.palette.mode === 'dark' ? 0.14 : 0.08)} 0%, transparent 60%)`,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
        <Box sx={{ width: 30, height: 30, borderRadius: 2, display: 'grid', placeItems: 'center', color, bgcolor: alpha(color, 0.15), '& svg': { fontSize: 18 } }}>
          {icon}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>{label}</Typography>
      </Stack>
      <Typography component="div" sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>{sub}</Typography>
      <Typography variant="caption" component="div" sx={{ mt: 0.25, fontWeight: 600 }}>{foot}</Typography>
    </Paper>
  );
}
